import { type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../utils.js'

export interface AppShellProps extends HTMLAttributes<HTMLDivElement> {
  /** Sidebar element */
  sidebar?: ReactNode
  /** Header element (mobile) */
  header?: ReactNode
  /** Desktop header element (full-width bar above everything) */
  desktopHeader?: ReactNode
  /** Whether the sidebar is collapsed */
  sidebarCollapsed?: boolean
  /** Right panel element (pushes content when open) */
  rightPanel?: ReactNode
}

/**
 * Main layout wrapper. Desktop: full-width header on top, then sidebar + content below.
 * Mobile: mobile header on top, content below, no sidebar.
 */
export function AppShell({
  sidebar,
  header,
  desktopHeader,
  sidebarCollapsed = false,
  rightPanel,
  className,
  children,
  ...props
}: AppShellProps) {
  return (
    <div className={cn('flex flex-col h-screen overflow-hidden bg-[var(--color-background)]', className)} {...props}>
      {/* Mobile header */}
      {header && (
        <header className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-background)] md:hidden">
          {header}
        </header>
      )}
      {/* Desktop header - full width, above sidebar + content */}
      {desktopHeader && (
        <header className="hidden md:block shrink-0 border-b border-[var(--color-border)] bg-[var(--color-background)]">
          {desktopHeader}
        </header>
      )}

      {/* Below header: sidebar + content side by side */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {sidebar && (
          <aside
            className={cn(
              'hidden md:flex flex-col shrink-0 border-r border-[var(--color-border-light)] bg-[var(--color-background)] transition-[width]',
              sidebarCollapsed ? 'w-14' : 'w-52',
            )}
          >
            {sidebar}
          </aside>
        )}

        {/* Content area - overflow-hidden ensures only the inner scroll container scrolls */}
        <main className="flex-1 min-h-0 overflow-hidden">{children}</main>

        {/* Right panel (notifications etc.) - pushes content */}
        {rightPanel}
      </div>
    </div>
  )
}
